import { useQuery } from '@tanstack/react-query';
import { Icoon } from '../ui/Icoon';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useActie, useIk } from '../lib/data';
import { toast } from '../ui/toast';
import { Badge, Laden, Modal, Tabs, Veld } from '../ui/ui';

interface Processen {
  modus: 'outlook' | 'test';
  eigenKoppelingen: number;
  mailVia: 'microsoft365' | 'smtp' | 'test';
  graphConsent: boolean;
  entraKoppelbaar: boolean;
  afzenderEmail: string | null;
  processen: { proces: string; laatsteRun: string | null; laatsteSucces: string | null; melding: string | null; aantal: number }[];
  fouten: { id: string; titel: string; fout: string | null }[];
  wachtrij: number;
}
interface Bericht { id: string; soort: string; aan: string; onderwerp: string; inhoud: string; start: string | null; eind: string | null; aangemaakt: string }

const UITLEG: Record<string, [string, string]> = {
  'outlook-sync': ['Planning naar Outlook', 'Direct na opslaan (en elke 2 min): afspraken maken, wijzigen, verwijderen'],
  'agenda-ophalen': ['Agenda ophalen', 'Elke 15 min: vrij/bezet van alle engineers, 3 maanden vooruit'],
  'outlook-naar-planning': ['Outlook naar planning', 'Elke 3 min: verschuivingen overnemen, verwijderde afspraken melden'],
  'zelf-ingepland': ['Zelf ingepland', 'Elke 30 min: afspraken met [projectnummer] in het onderwerp → overleg + urenlink'],
  herinnering: ['Herinnering terugmelding', 'Werkdagen 08:00: mail met openstaande terugmeldingen'],
  'afwezigheid-sync': ['Afwezigheid naar Outlook', 'Verlof en ziekte als afwezig in de agenda'],
  'feestdagen-sync': ['Feestdagen naar Outlook', 'Vrije dagen in ieders agenda'],
  geocode: ['Geocode en reistijd', 'Elke 5 min: adressen opzoeken en reistijd per taak herrekenen'],
  prognose: ['Prognose projecten', 'Elke 30 min: verwachte einddatum per project herberekenen'],
  'uren-herinnering': ['Uren-herinneringen', 'Elke 15 min: mail bij een vergeten timer; vrijdag vanaf 14:00 bij een onvolledige weekstaat'],
};

export function Koppelingen() {
  const ik = useIk().data!;
  const [q] = useSearchParams();
  const [tab, setTab] = useState<'outlook' | 'processen' | 'uitgaand'>('outlook');
  const p = useQuery({ queryKey: ['processen'], queryFn: () => api.get<Processen>('/api/beheer/processen'), refetchInterval: 10000 });
  const uit = useQuery({ queryKey: ['uitgaand'], queryFn: () => api.get<Bericht[]>('/api/beheer/uitgaand'), enabled: tab === 'uitgaand', refetchInterval: 10000 });
  const [afzender, setAfzender] = useState<string | null>(null);
  const [toon, setToon] = useState<Bericht | null>(null);
  const draai = useActie((n: string) => api.post(`/api/beheer/processen/${n}`), { succes: 'Proces gestart' });
  const bewaar = useActie(() => api.put('/api/organisatie', { naam: ik.organisatie.naam, afzenderEmail: afzender || null }), { succes: 'Opgeslagen', opNa: () => p.refetch() });
  const testmail = useActie(() => api.post<{ via: string; aan: string }>('/api/beheer/testmail'), {
    succes: (r) => (r.via === 'test' ? 'Testmail staat in het testpostvak' : `Testmail verstuurd naar ${r.aan}`),
  });
  const ontkoppel = useActie(() => api.post('/api/organisatie/outlook-ontkoppelen'), { succes: 'Outlook ontkoppeld (testmodus)', opNa: () => p.refetch() });
  useEffect(() => {
    if (q.get('consent') === 'ok') toast('Outlook is gekoppeld!', 'succes');
    if (q.get('consent') === 'fout') toast(`Koppelen mislukt: ${q.get('melding') ?? ''}`, 'fout');
  }, [q]);
  if (!p.data) return <Laden />;
  const d = p.data;
  return (
    <div className="pagina" style={{ maxWidth: 1200 }}>
      <div className="paneel">
        <div className="sectie-kop">
          <h1 className="groei">Outlook en processen</h1>
          {d.modus === 'outlook' ? (
            <Badge toon="groen">Microsoft 365 gekoppeld</Badge>
          ) : d.eigenKoppelingen > 0 ? (
            <Badge toon="grijs" title="Engineers zonder eigen agenda staan in testmodus; hun afspraken komen in het testpostvak">Geen Microsoft 365-organisatiekoppeling</Badge>
          ) : (
            <Badge toon="oranje">Testmodus – nog geen agenda gekoppeld</Badge>
          )}
          {d.eigenKoppelingen > 0 && <Badge toon="groen">{d.eigenKoppelingen} eigen {d.eigenKoppelingen === 1 ? 'agenda' : "agenda's"} gekoppeld</Badge>}
        </div>
        <Tabs waarde={tab} onChange={setTab} opties={[['outlook', 'Outlook-koppeling'], ['processen', 'Processen'], ['uitgaand', 'Testpostvak']]} />
        {tab === 'outlook' && (
          <div className="kolom" style={{ maxWidth: 760 }}>
            <p>
              De app zet geplande taken, verlof en vrije dagen in de Outlook-agenda's van de engineers, leest hun vrij/bezet en verstuurt herinneringen. Daarvoor geeft een beheerder van jullie Microsoft 365 eenmalig toestemming
              (<i>admin consent</i>) aan de app. De rechten: agenda's lezen en schrijven, mail versturen namens de afzender, gebruikers lezen en Teams-vergaderingen maken.
            </p>
            <div className="paneel vlak klein">
              Geen zakelijke Microsoft 365, of wil een engineer een andere agenda gebruiken? Dan koppelt de engineer <b>zijn eigen agenda</b> via <i>Mijn agenda</i>, of je stuurt hem een koppellink bij <i>Beheer › Engineers</i>. Dat werkt ook met privéaccounts (Outlook.com, Office 365 Family).
            </div>
            {!d.entraKoppelbaar && <div className="paneel vlak" style={{ background: 'var(--oranje-bg)' }}>De Microsoft-koppeling is nog niet ingesteld op de server (ENTRA_CLIENT_ID/SECRET). Zie de README.</div>}
            {d.entraKoppelbaar && !d.graphConsent && (
              <a className="knop primair" href="/auth/consent" style={{ alignSelf: 'flex-start' }}>
                Outlook koppelen (admin consent)
              </a>
            )}
            {d.graphConsent && (
              <div className="rij">
                <Badge toon="groen">✓ Toestemming gegeven</Badge>
                <button className="knop gevaar klein" onClick={() => ontkoppel.mutate()}>Ontkoppelen</button>
              </div>
            )}
            <h3 style={{ marginTop: 8 }}>Mail</h3>
            <div className="rij wrap">
              <span>Herinneringen, urenlinks en koppellinks gaan via</span>
              <Badge toon={d.mailVia === 'test' ? 'oranje' : 'groen'}>
                {d.mailVia === 'microsoft365' ? 'Microsoft 365 (jullie afzender)' : d.mailVia === 'smtp' ? 'de mailserver van bsmartr' : 'het testpostvak'}
              </Badge>
              <button className="knop klein" onClick={() => testmail.mutate()}><Icoon naam="mail" maat={14} /> Testmail naar mij</button>
            </div>
            <Veld
              label={d.mailVia === 'smtp' ? 'Antwoordadres van mails' : 'Afzender van mails (mailbox in jullie Microsoft 365)'}
              hint={
                d.mailVia === 'smtp'
                  ? 'Mails komen van de mailserver van bsmartr; antwoorden gaan naar dit adres, bijv. planning@jullie-bedrijf.nl'
                  : 'Herinneringen terugmelding en urenlinks worden vanuit deze mailbox gestuurd, bijv. planning@jullie-bedrijf.nl'
              }
            >
              <div className="rij">
                <input value={afzender ?? d.afzenderEmail ?? ''} onChange={(e) => setAfzender(e.target.value)} />
                <button className="knop" onClick={() => bewaar.mutate()}>Opslaan</button>
              </div>
            </Veld>
          </div>
        )}
        {tab === 'processen' && (
          <>
            <p className="klein mute">Deze processen vervangen de Power Automate-flows. Ze draaien automatisch; met "Nu uitvoeren" start je er één direct. In de wachtrij: {d.wachtrij} taken.</p>
            <table className="tabel">
              <thead><tr><th>Proces</th><th>Wanneer</th><th>Laatste run</th><th>Resultaat</th><th /></tr></thead>
              <tbody>
                {d.processen.map((x) => (
                  <tr key={x.proces}>
                    <td className="vet">{UITLEG[x.proces]?.[0] ?? x.proces}</td>
                    <td className="klein">{UITLEG[x.proces]?.[1]}</td>
                    <td className="klein nowrap">{x.laatsteRun ? new Date(x.laatsteRun).toLocaleString('nl-NL', { dateStyle: 'short', timeStyle: 'medium' }) : 'nog niet'}</td>
                    <td className="klein">{x.laatsteRun && x.laatsteSucces !== x.laatsteRun ? <span style={{ color: 'var(--rood)' }}>⚠ {x.melding}</span> : x.melding}</td>
                    <td><button className="knop klein" onClick={() => draai.mutate(x.proces)}>Nu uitvoeren</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
            {d.fouten.length > 0 && (
              <>
                <h3 style={{ marginTop: 14 }}>Taken met een Outlook-fout</h3>
                <ul>{d.fouten.map((f) => <li key={f.id}><b>{f.titel}</b>: {f.fout}</li>)}</ul>
              </>
            )}
          </>
        )}
        {tab === 'uitgaand' && (
          <>
            <p className="klein mute">In testmodus komen afspraken en mails hier terecht in plaats van in Outlook. Zo kun je alles uitproberen zonder echte agenda's te raken.</p>
            <table className="tabel">
              <thead><tr><th>Tijd</th><th>Soort</th><th>Aan</th><th>Onderwerp</th><th>Wanneer</th></tr></thead>
              <tbody>
                {(uit.data ?? []).map((b) => (
                  <tr key={b.id} className="klik" onClick={() => setToon(b)}>
                    <td className="klein nowrap">{new Date(b.aangemaakt).toLocaleString('nl-NL', { dateStyle: 'short', timeStyle: 'medium' })}</td>
                    <td><Badge toon={b.soort === 'mail' ? 'blauw' : b.soort === 'verwijderd' ? 'rood' : 'groen'}>{b.soort}</Badge></td>
                    <td className="klein">{b.aan}</td>
                    <td>{b.onderwerp}</td>
                    <td className="klein nowrap">{b.start}{b.eind ? ` → ${b.eind}` : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!uit.data?.length && <div className="leeg">Nog niets verstuurd.</div>}
          </>
        )}
      </div>
      {toon && (
        <Modal titel={toon.onderwerp} onSluit={() => setToon(null)}>
          <div className="klein mute" style={{ marginBottom: 8 }}>Aan: {toon.aan} {toon.start ? `· ${toon.start} → ${toon.eind}` : ''}</div>
          <div dangerouslySetInnerHTML={{ __html: toon.inhoud }} />
        </Modal>
      )}
    </div>
  );
}
